import axios from 'axios';
import { getAccessToken } from './auth';

export interface Location {
    _id: string;
    country: string;
    city: string;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// Create axios instance with base configuration
export const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    }
});

// External APIs

const geoDbApi = axios.create({
    baseURL: 'https://wft-geo-db.p.rapidapi.com/v1',
    headers: {
        'X-RapidAPI-Key': import.meta.env.VITE_GEODB_API_KEY || '',
        'X-RapidAPI-Host': 'wft-geo-db.p.rapidapi.com'
    }
});

export interface Country {
    name: {
        common: string;
    };
    cca2: string;
}

export interface City {
    id: number;
    wikiDataId: string;
    name: string;
    country: string;
    countryCode: string;
    latitude: number;
    longitude: number;
    timezone: string;
    population: number;
}

export interface GeoDBResponse {
    data: City[];
    metadata: {
        totalCount: number;
        offset: number;
        limit: number;
    };
}

interface GeoDBSingleCityResponse {
    data: City;
}

// Helper function to delay execution
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Add request interceptor to add auth token
api.interceptors.request.use(async (config) => {
    try {
        // Get the access token from Auth0
        const token = await getAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
    } catch (error) {
        console.error('Error getting access token:', error);
    }
    return config;
});

// Add response interceptor for error handling
api.interceptors.response.use(
    response => response,
    async error => {
        if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            console.error('API Error:', error.response.data);
            
            // Handle authentication errors
            if (error.response.status === 401) {
                // Prevent infinite retry loops - only retry once
                if (error.config._retryCount) {
                    console.error('Already retried, giving up on auth retry');
                    return Promise.reject(error);
                }
                
                // Try to get a new token
                try {
                    const token = await getAccessToken();
                    if (token) {
                        // Retry the original request with the new token
                        const originalRequest = error.config;
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        originalRequest._retryCount = true; // Mark as retried
                        return api(originalRequest);
                    } else {
                        // If no token available, let the user handle authentication
                        console.log('No valid token available, user needs to re-authenticate');
                        return Promise.reject(error);
                    }
                } catch (tokenError) {
                    console.error('Error refreshing token:', tokenError);
                    return Promise.reject(error);
                }
            }
        } else if (error.request) {
            // The request was made but no response was received
            console.error('Network Error:', error.request);
        } else {
            // Something happened in setting up the request that triggered an Error
            console.error('Request Error:', error.message);
        }
        return Promise.reject(error);
    }
);

export interface Zikr {
    id?: string;
    arabic: string;
    translation: string;
    transliteration?: string;
    category: string;
    count?: number;  // Recommended repetitions
    virtue?: string; // Benefits/rewards of this zikr
    created_at?: string;
    updated_at?: string;
    visibility_schedule?: { [day: string]: number[] } | null;
}

export interface IslamicEvent {
    id?: string;
    title: string;
    description: string;
    greg_date?: string;
    hijri_date?: string;
    type: 'holiday' | 'special_day' | 'historic';
    created_at?: string;
    updated_at?: string;
    visibility_schedule?: { [day: string]: number[] } | null;
}

interface PrayerTime {
    date: string;
    fajr: string;
    sunrise: string;
    dhuhr: string;
    asr: string;
    maghrib: string;
    isha: string;
}

// Remove the nested structure since the backend returns the array directly
type PrayerTimesResponse = PrayerTime[];

export interface BrandingSettings {
    mosque_name: string;
    primary_color: string;
    secondary_color: string;
    logo_path?: string;
}

export interface Media {
    _id: string;
    file_id: string;
    file_name: string;
    file_size: number;
    mime_type: string;
    file_type: string;
    title?: string;
    description?: string;
    publish_date?: string;
    unpublish_date?: string;
    is_active: boolean;
    is_archived: boolean;
    display_duration?: number; // Duration in seconds to display in carousel (default 15 seconds for media)
    padding_analysis?: {
        dimensions: {
            width: number;
            height: number;
            aspect_ratio: number;
        };
        std_deviations: {
            top: number;
            bottom: number;
            center: number;
        };
        padding_detected: {
            top: boolean;
            bottom: boolean;
            overall: boolean;
        };
        aspect_ratio_analysis: {
            is_a4_like: boolean;
            a4_aspect_ratio: number;
            difference_from_a4: number;
            tolerance: number;
        };
        suggested_crop?: {
            coordinates: {
                x: number;
                y: number;
                width: number;
                height: number;
            };
            original_aspect_ratio: number;
            suggested_aspect_ratio: number;
            crop_percentage: {
                top: number;
                bottom: number;
            };
            a4_suggestions: {
                needed: boolean;
                reason?: string;
                direction?: 'horizontal' | 'vertical';
                coordinates?: {
                    x: number;
                    y: number;
                    width: number;
                    height: number;
                };
                crop_percentage?: {
                    left: number;
                    right: number;
                    top: number;
                    bottom: number;
                };
                resulting_aspect_ratio?: number;
            };
        };
        error?: string;
    }
    created_at: string;
    updated_at?: string;
    uploaded_by?: string;
    updated_by?: string;
    deactivated_reason?: string;
    visibility_schedule?: { [day: string]: number[] } | null;
}

// Helper function to get authenticated headers
const getAuthHeaders = async () => {
    const token = await getAccessToken();
    return {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
    };
};

// Helper function for multipart requests

export const apiMethods = {
    // External APIs
    getCountries: async (): Promise<Country[]> => {
        const response = await api.get('/locations/countries');
        // Transform the response to match the Country interface
        return response.data.map((countryName: string) => ({
            name: { common: countryName },
            cca2: countryName.substring(0, 2).toUpperCase() // Simple 2-letter code from country name
        }));
    },

    getCities: async (countryCode: string, searchText: string): Promise<City[]> => {
        // If search text is less than 2 characters, return empty array
        if (searchText.length < 2) {
            return [];
        }

        let retryCount = 0;
        const maxRetries = 3;
        let retryDelay = 5000; // Start with 5 seconds if we hit rate limit

        while (retryCount < maxRetries) {
            try {
                // Add 1 second delay before each request to avoid rate limits
                await delay(1000);

                const params = new URLSearchParams({
                    countryIds: countryCode,
                    limit: '5',
                    offset: '0',
                    minPopulation: '100000',
                    namePrefix: searchText
                });

                const response = await geoDbApi.get<GeoDBResponse>(
                    `/geo/cities?${params.toString()}`
                );
                
                return response.data.data;
            } catch (error) {
                retryCount++;
                
                if (axios.isAxiosError(error) && error.response?.status === 429) {
                    console.log(`Rate limited, waiting ${retryDelay/1000} seconds before retry ${retryCount}`);
                    await delay(retryDelay);
                    retryDelay *= 2; // Double the delay for next attempt
                    if (retryCount < maxRetries) continue;
                }
                
                if (retryCount === maxRetries) {
                    throw error;
                }
            }
        }

        return [];
    },

    getCityDetails: async (wikiDataId: string) => {
        let retryCount = 0;
        const maxRetries = 3;
        let retryDelay = 5000; // Start with 5 seconds if we hit rate limit

        while (retryCount < maxRetries) {
            try {
                // Add 1 second delay before each request to avoid rate limits
                await delay(1000);

                const response = await geoDbApi.get<GeoDBSingleCityResponse>(
                    `/geo/cities/${wikiDataId}`
                );
                
                const cityData = response.data.data;
                if (!cityData.latitude || !cityData.longitude) {
                    throw new Error('City coordinates not found');
                }
                
                return cityData;
            } catch (error) {
                retryCount++;
                
                if (axios.isAxiosError(error)) {
                    if (error.response?.status === 429) {
                        console.log(`Rate limited, waiting ${retryDelay/1000} seconds before retry ${retryCount}`);
                        await delay(retryDelay);
                        retryDelay *= 2;
                        if (retryCount < maxRetries) continue;
                    } else if (error.response?.status === 404) {
                        throw new Error('City not found');
                    }
                }
                
                if (retryCount === maxRetries) {
                    throw error;
                }
            }
        }

        throw new Error('Failed to get city details after max retries');
    },

    // Locations
    getAllLocations: async (): Promise<Location[]> => {
        try {
            const response = await api.get('/locations/');
            return response.data;
        } catch (error) {
            console.error('Error fetching locations:', error);
            throw error;
        }
    },

    createLocation: async (data: { country: string; city: string }): Promise<Location> => {
        const response = await api.post('/locations', data);
        return response.data;
    },

    getLocationsByCity: async (country: string, city: string) => {
        const response = await api.get(`/locations/${country}/${city}`);
        return response.data;
    },

    // Prayer Times
    uploadPrayerTimes: async (locationId: string, formData: FormData) => {
        const response = await api.post(`/prayer-times/upload/${locationId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    listPrayerTimes: async (locationId: string): Promise<PrayerTimesResponse> => {
        const response = await api.get(`/prayer-times/list/${locationId}`);
        // Add error checking for the response
        if (!Array.isArray(response.data)) {
            console.error('Unexpected prayer times response format:', response.data);
            throw new Error('Invalid prayer times response format');
        }
        return response.data;
    },

    getDailyPrayerTimes: async (date: string, locationId: string): Promise<PrayerTime> => {
        const response = await api.get(`/prayer-times/${locationId}/${date}`);
        return response.data;
    },

    // Zikr endpoints
    getZikr: async (category?: string): Promise<Zikr[]> => {
        const response = await api.get('/zikr', {
            params: { category }
        });
        return response.data;
    },

    createZikr: async (zikr: Omit<Zikr, 'id'>): Promise<Zikr> => {
        const response = await api.post('/zikr', zikr);
        return response.data;
    },

    updateZikr: async (id: string, zikr: Omit<Zikr, 'id'>): Promise<Zikr> => {
        const response = await api.put(`/zikr/${id}`, zikr);
        return response.data;
    },

    deleteZikr: async (id: string): Promise<void> => {
        await api.delete(`/zikr/${id}`);
    },

    // Event endpoints
    getEvents: async (): Promise<IslamicEvent[]> => {
        const response = await api.get('/events');
        return response.data;
    },

    createEvent: async (event: Omit<IslamicEvent, 'id' | 'created_at' | 'updated_at'>): Promise<IslamicEvent> => {
        const response = await api.post('/events', event);
        return response.data;
    },

    updateEvent: async (id: string, event: Omit<IslamicEvent, 'id' | 'created_at' | 'updated_at'>): Promise<IslamicEvent> => {
        const response = await api.put(`/events/${id}`, event);
        return response.data;
    },

    deleteEvent: async (id: string): Promise<void> => {
        await api.delete(`/events/${id}`);
    },

    getUpcomingEvents: async (limit: number = 5): Promise<IslamicEvent[]> => {
        const response = await api.get('/events/upcoming', { params: { limit } });
        return response.data;
    },

    // Branding API
    getBrandingSettings: async (): Promise<BrandingSettings> => {
        const response = await api.get('/branding');
        return response.data;
    },

    updateBrandingSettings: async (data: FormData): Promise<BrandingSettings> => {
        const response = await api.post('/branding', data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    uploadLogo: async (data: FormData): Promise<{ logo_path: string }> => {
        const response = await api.post('/branding/logo', data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    getLogoUrl: (logoPath: string): string => {
        if (!logoPath) return '';
        const filename = logoPath.split('/').pop();
        return `${API_BASE_URL}/branding/logo/${filename}`;
    },

    // Hijri Settings API
    getHijriSettings: async (): Promise<{ hijri_offset: number }> => {
        const response = await api.get('/settings/hijri');
        return response.data;
    },

    getHijriCalendar: async (year: number): Promise<any> => {
        const response = await api.get(`/settings/hijri-calendar/${year}`);
        return response.data;
    },

    updateHijriOffset: async (hijri_offset: number): Promise<{ hijri_offset: number; success: boolean }> => {
        const response = await api.put('/settings/hijri', { hijri_offset });
        return response.data;
    },

    // Legacy hijri adjustment (keeping for compatibility)
    setHijriAdjustment: async (data: { days: number }): Promise<void> => {
        await api.post('/hijri-adjustment', data);
    },

    // Media API
    getMedia: async (): Promise<Media[]> => {
        const response = await api.get('/media/');
        return response.data;
    },

    getActiveMedia: async (): Promise<Media[]> => {
        const response = await api.get('/media/active');
        return response.data;
    },

    getArchivedMedia: async (skip = 0, limit = 50): Promise<Media[]> => {
        const response = await api.get(`/media/archived?skip=${skip}&limit=${limit}`);
        return response.data;
    },

    getMediaStatusSummary: async (): Promise<any> => {
        const response = await api.get('/media/status/summary');
        return response.data;
    },

    cleanupExpiredMedia: async (action = 'archive_expired', gracePeriodDays = 0): Promise<any> => {
        const response = await api.post('/media/cleanup/expired', { action, grace_period_days: gracePeriodDays });
        return response.data;
    },

    archiveSelectedMedia: async (mediaIds: string[]): Promise<any> => {
        const response = await api.post('/media/archive/selected', { media_ids: mediaIds });
        return response.data;
    },

    deactivateSelectedMedia: async (mediaIds: string[], reason?: string): Promise<any> => {
        const response = await api.post('/media/deactivate/selected', { media_ids: mediaIds, reason: reason || 'manual_deactivation' });
        return response.data;
    },

    uploadMedia: async (formData: FormData): Promise<Media> => {
        const response = await api.post('/media/upload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    updateMedia: async (id: string, data: Partial<Media>): Promise<Media> => {
        const response = await api.put(`/media/${id}`, data);
        return response.data;
    },

    deleteMedia: async (id: string): Promise<void> => {
        await api.delete(`/media/${id}`);
    },

    downloadMedia: async (id: string): Promise<Blob> => {
        const headers = await getAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/media/${id}/download`, { headers });
        if (!response.ok) throw new Error('Failed to download media');
        return response.blob();
    },

    getMediaById: async (id: string): Promise<Media> => {
        const response = await api.get(`/media/${id}`);
        return response.data;
    },

    restoreSelectedMedia: async (mediaIds: string[]): Promise<{ message: string; processed: number; errors: number }> => {
        const response = await api.post('/media/restore/selected', { media_ids: mediaIds });
        return response.data;
    },

    deleteArchivedMedia: async (): Promise<any> => {
        const response = await api.post('/media/delete/archived');
        return response.data;
    },

    // Padding Analysis API
    analyzePadding: async (mediaId: string, thresholdRatio = 0.4, a4Tolerance = 0.05): Promise<{
        media_id: string;
        has_padding: boolean;
        analysis: any;
        updated: boolean;
    }> => {
        const response = await api.post(`/media/${mediaId}/analyze-padding?threshold_ratio=${thresholdRatio}&a4_tolerance=${a4Tolerance}`);
        return response.data;
    },

    analyzeAllPadding: async (thresholdRatio = 0.4, a4Tolerance = 0.05, skipExisting = true): Promise<{
        total_images: number;
        analyzed: number;
        with_padding: number;
        errors: number;
        details: Array<{
            media_id: string;
            filename: string;
            has_padding?: boolean;
            dimensions?: any;
            error?: string;
        }>;
    }> => {
        const response = await api.post(`/media/analyze-all-padding?threshold_ratio=${thresholdRatio}&a4_tolerance=${a4Tolerance}&skip_existing=${skipExisting}`);
        return response.data;
    },

    // New Unified Image Analysis API
    validateImage: async (file: File, strictA4 = true, rejectPadding = true): Promise<{
        filename: string;
        validation_result: {
            is_acceptable: boolean;
            basic_info: any;
            a4_check: any;
            padding_check: any;
            rejection_reasons: string[];
        };
        available_methods: any;
    }> => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('strict_a4', strictA4.toString());
        formData.append('reject_padding', rejectPadding.toString());
        
        const response = await api.post('/media/validate-image', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    analyzeImage: async (file: File, preferOpencv = false): Promise<{
        filename: string;
        has_padding: boolean;
        analysis_data: any;
        available_methods: any;
    }> => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('prefer_opencv', preferOpencv.toString());
        
        const response = await api.post('/media/analyze-image', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    getAnalysisMethods: async (): Promise<{
        available_methods: {
            opencv: boolean;
            pil: boolean;
            recommended: string;
        };
    }> => {
        const response = await api.get('/media/analysis-methods');
        return response.data;
    },

    // TV Remote Refresh
    triggerTvRefresh: async (type: 'soft' | 'hard' = 'soft'): Promise<{
        message: string;
        connected_clients: number;
    }> => {
        const response = await api.post('/tv/refresh', { type });
        return response.data;
    },

    getTvConnections: async (): Promise<{
        connected_clients: number;
        connections: Array<{ location_id: string; connected_at: string }>;
    }> => {
        const response = await api.get('/tv/connections');
        return response.data;
    },
}; 